import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, X, ShieldCheck } from "lucide-react";

export default function AdminPseudos() {
  const [users, setUsers] = useState([]);
  const [edits, setEdits] = useState({});

  const load = () =>
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const pending = users.filter((u) => u.pseudo_status === "pending");

  const moderate = async (uid, action) => {
    try {
      await api.post("/admin/moderate-pseudo", {
        user_id: uid, action, new_pseudo: edits[uid] || undefined,
      });
      toast.success(action === "approve" ? "Pseudo approuvé." : "Pseudo modifié.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-12">
      <header>
        <div className="phenix-eyebrow mb-3">Modération</div>
        <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
          <em className="text-pink-500">Pseudos</em> en attente.
        </h1>
        <p className="mt-6 text-base text-plum-700/70 max-w-xl leading-relaxed">
          Validez ou corrigez les pseudos publics avant qu'ils ne soient affichés sur la plateforme.
        </p>
      </header>

      {pending.length === 0 ? (
        <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50">
          <ShieldCheck size={28} strokeWidth={1} className="mx-auto mb-4 text-pink-500" />
          <p className="italic">Tous les pseudos sont à jour.</p>
        </div>
      ) : (
        <div className="divide-y divide-cream-200 border-y border-cream-200">
          {pending.map((u, i) => (
            <div key={u.id} className="grid lg:grid-cols-12 gap-6 py-8 items-center" data-testid={`pseudo-row-${u.id}`}>
              <div className="lg:col-span-1 font-mono text-[11px] text-plum-700/50">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="lg:col-span-4">
                <div className="font-display text-xl text-ink-900">{u.name}</div>
                <div className="text-[11px] text-plum-700/50">{u.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-plum-700 mt-1">{u.role}</div>
              </div>
              <div className="lg:col-span-4">
                <div className="phenix-eyebrow mb-1">Pseudo proposé</div>
                <Input
                  defaultValue={u.pseudo}
                  onChange={(e) => setEdits({ ...edits, [u.id]: e.target.value })}
                  data-testid={`pseudo-input-${u.id}`}
                  className="rounded-xl border-cream-200 bg-transparent h-10 font-mono"
                />
              </div>
              <div className="lg:col-span-3 flex gap-2 justify-end">
                <Button
                  size="sm" onClick={() => moderate(u.id, "approve")}
                  className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-9"
                  data-testid={`pseudo-approve-${u.id}`}
                >
                  <Check size={12} className="mr-1.5" /> Approuver
                </Button>
                <Button
                  size="sm" variant="ghost" onClick={() => moderate(u.id, "reject")}
                  className="rounded-xl h-9"
                  data-testid={`pseudo-reject-${u.id}`}
                >
                  <X size={12} className="mr-1.5" /> Rejeter
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
